import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { apiRequest } from "../../api/apiHelper";

import { getClientCompane } from "../../api/apiHelper";

export default function Clientonboarding() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [activeButton, setActiveButton] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newClient, setNewClient] = useState({
    companyName: "",
    address: "",
    contactNumbers: [{ name: "", number: "", email: "" }],
    OrganizationId: null,
    dealIds: [],
    website: "",
  });
  const [openAddModal, setOpenAddModal] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Transform API data to match your table structure
  const transformApiData = (apiResponse) => {
    if (!apiResponse.data || !apiResponse.data.companies) return [];

    return apiResponse.data.companies.map((company, index) => ({
      id: company._id,
      customerId: company.customerId || "",
      name: company.companyName,
      projectTitle:
        company.dealIds && company.dealIds.length > 0
          ? `${company.dealIds.length} Active Deal${company.dealIds.length > 1 ? "s" : ""
          }`
          : "No Active Deals",
      status:
        company.dealIds && company.dealIds.length > 0 ? "Active" : "Inactive",
      email: company.contactNumbers?.[0]?.email || "",
      lastContacted: new Date(company.updatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      nextFollowUp: "Not Scheduled",
      recentActivity: `${company.dealIds ? company.dealIds.length : 0} deals`,
      address: company.address,
      contactNumbers: company.contactNumbers,
      dealIds: company.dealIds,
      createdAt: company.createdAt,
      updatedAt: company.updatedAt,
    }));
  };

  const fetchClientsData = async () => {
    setLoading(true);
    try {

      const response = await getClientCompane.getClientCompanies()
      const transformedData = transformApiData(response);
      setClients(transformedData);
    } catch (error) {
      console.error("Error fetching clients:", error);
      showNotification("Error fetching clients. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientsData();
  }, []);

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const isSelected = (email) => selected.indexOf(email) !== -1;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(clients.map((c) => c.email));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (email) => {
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const handleExportCSV = () => {
    const headers = [
      "id",
      "name",
      "projectTitle",
      "status",
      "email",
      "lastContacted",
      "nextFollowUp",
      "recentActivity",
      "address",
    ];

    const rows = clients.map((c) =>
      headers.map((h) => `"${c[h] ?? ""}"`).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "clients.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDeactivate = () => {
    setClients((prev) =>
      prev.map((c) =>
        selected.includes(c.email) ? { ...c, status: "Inactive" } : c
      )
    );
    setSelected([]);
  };

  const filteredClients = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleViewActivities = (clientId) => {
    navigate(`/home?clientId=${clientId}`);
  };

  const handleRefresh = () => {
    fetchClientsData();
  };

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setNewClient({
      companyName: "",
      address: "",
      contactNumbers: [{ name: "", number: "", email: "" }],
      OrganizationId: null,
      dealIds: [],
      website: "",
    });
  };

  const handleInputChange = (field, value) => {
    setNewClient((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactChange = (index, field, value) => {
    setNewClient((prev) => ({
      ...prev,
      contactNumbers: prev.contactNumbers.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      ),
    }));
  };

  const addContactField = () => {
    setNewClient((prev) => ({
      ...prev,
      contactNumbers: [...prev.contactNumbers, { name: "", number: "", email: "" }],
    }));
  };

  const removeContactField = (index) => {
    if (newClient.contactNumbers.length > 1) {
      setNewClient((prev) => ({
        ...prev,
        contactNumbers: prev.contactNumbers.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmitClient = async () => {
    try {
      // Validate required fields
      if (!newClient.companyName) {
        showNotification("Company name is required", "error");
        return;
      }

      setSubmitting(true);
      const orgId = localStorage.getItem("orgId");
      // Create payload matching your API structure
      const payload = {
        OrganizationId: orgId,
        companyName: newClient.companyName,
        contactNumbers: newClient.contactNumbers.filter(
          (contact) => contact.name && contact.number && contact.email
        ),
        website: newClient.website || "",
        address: newClient.address,
        dealIds: newClient.dealIds,
      };

      // API call to create client
      const response = await getClientCompane.createCompany(payload)


      if (response) {
        showNotification("Client created successfully!", "success");
        handleCloseAddModal();
        fetchClientsData(); // Refresh the list
      }
    } catch (error) {
      console.error("Error creating client:", error);
      showNotification("Error creating client. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          mx: "auto",
          mt: 2,
          padding: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading clients...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", mx: "auto", paddingTop: 0, paddingBottom: 2, overflowX: "hidden" }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 550, fontSize: 28, color: "#1e293b" }}>
        Clients ({clients.length})
      </Typography>

      <Toolbar sx={{ px: 0, mb: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <TextField
          placeholder="Search clients"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />

        <Box>
          <Button variant="outlined" onClick={handleRefresh} sx={{ ml: 2 }}>
            Refresh
          </Button>

          <Button
            variant={activeButton === "export" ? "contained" : "outlined"}
            color="primary"
            onClick={() => {
              handleExportCSV();
              setActiveButton("export");
            }}
            sx={{ ml: 2 }}
          >
            Export CSV
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
            sx={{
              backgroundColor: "#396afc",
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              py: 1,
              ml: 2,
              "&:hover": {
                backgroundColor: "#2557eb",
              },
            }}
          >
            Add Client
          </Button>
        </Box>

        {selected.length > 0 && (
          <>
            <Typography sx={{ ml: 2 }}>{selected.length} Selected</Typography>
            <Button onClick={handleSelectAll} sx={{ ml: 1 }}>
              Select All
            </Button>
            <Button onClick={handleDeactivate} color="error" sx={{ ml: 1 }}>
              Deactivate
            </Button>
          </>
        )}
      </Toolbar>

      <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: "auto" }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead
            sx={{
              backgroundColor: "#f1efefff",
              "& .MuiTableCell-root": {
                fontWeight: "bold",
                color: "#000",
              },
            }}
          >
            <TableRow>
              <TableCell>Customer ID</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Project Status</TableCell>
              <TableCell>Contact Info</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell>Next Follow-up</TableCell>
              <TableCell>Recent Activities</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No clients found {search && `matching "${search}"`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id} hover sx={{ cursor: "pointer" }}>
                  <TableCell
                    sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                  >
                    {client.customerId || client.id.slice(-8) + "..."}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {client.name}
                      </Typography>
                      {client.address && (
                        <Typography variant="caption" color="text.secondary">
                          {client.address}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.status}
                      color={client.status === "Active" ? "success" : "default"}
                      size="small"
                      sx={{
                        backgroundColor:
                          client.status === "Active" ? "#e6f9f2" : "#ffeaea",
                        color:
                          client.status === "Active" ? "#3ecf8e" : "#ff4d6d",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <a
                        href={`mailto:${client.email}`}
                        style={{ color: "#396afc", textDecoration: "none" }}
                      >
                        {client.email}
                      </a>
                      {client.contactNumbers &&
                        client.contactNumbers.length > 0 && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {client.contactNumbers[0].name}:{" "}
                            {client.contactNumbers[0].number}
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell>{client.lastContacted}</TableCell>
                  <TableCell>{client.nextFollowUp}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {client.recentActivity}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Client Modal - Vertical Format */}
      <Dialog
        open={openAddModal}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            pb: 1,
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <BusinessIcon sx={{ color: "#396afc", fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600} color="#2c3e50">
              Add New Client
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new client company profile
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseAddModal}
            sx={{
              color: "grey.500",
              "&:hover": { backgroundColor: "grey.100" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Stack spacing={3}>
            {/* Company Information Section */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <BusinessIcon sx={{ fontSize: 20, color: "#396afc" }} />
                Company Information
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Company Name"
                  variant="outlined"
                  required
                  value={newClient.companyName}
                  onChange={(e) =>
                    handleInputChange("companyName", e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                {/* NEW: Website */}
                <TextField
                  fullWidth
                  label="Website"
                  variant="outlined"
                  type="url"
                  placeholder="https://example.com"
                  value={newClient.website || ""}
                  onChange={(e) => handleInputChange("website", e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />

                <TextField
                  fullWidth
                  label="Address"
                  variant="outlined"
                  multiline
                  rows={3}
                  value={newClient.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  placeholder="Enter company address..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Contact Information Section */}
            {/* <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <EmailIcon sx={{ fontSize: 20, color: "#396afc" }} />
                Contact Information
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  required
                  type="email"
                  value={newClient.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Stack>
            </Box> */}

            <Divider />

            {/* Contact Numbers Section */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <PhoneIcon sx={{ fontSize: 20, color: "#396afc" }} />
                Contact Numbers
              </Typography>
              <Stack spacing={2}>
                {newClient.contactNumbers.map((contact, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      border: "1px solid #e9ecef",
                      borderRadius: 2,
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Contact Name"
                        variant="outlined"
                        size="small"
                        value={contact.name}
                        onChange={(e) =>
                          handleContactChange(index, "name", e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: "white",
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        size="small"
                        value={contact.number}
                        onChange={(e) =>
                          handleContactChange(index, "number", e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: "white",
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Contact Email"
                        variant="outlined"
                        size="small"
                        type="email"
                        value={contact.email || ""}
                        onChange={(e) => handleContactChange(index, "email", e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, backgroundColor: "white" } }}
                      />
                      {newClient.contactNumbers.length > 1 && (
                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="text"
                            color="error"
                            size="small"
                            onClick={() => removeContactField(index)}
                            startIcon={<CloseIcon />}
                            sx={{ textTransform: "none" }}
                          >
                            Remove Contact
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                ))}

                <Button
                  variant="outlined"
                  size="medium"
                  onClick={addContactField}
                  startIcon={<AddIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderStyle: "dashed",
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                  }}
                >
                  Add Another Contact
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, backgroundColor: "#f8f9fa" }}>
          <Button
            onClick={handleCloseAddModal}
            variant="outlined"
            disabled={submitting}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitClient}
            variant="contained"
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              backgroundColor: "#396afc",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "#2557eb",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {submitting ? "Creating..." : "Create Client"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
